/**
 * AlertHistoryWidget — Timeline of all alerts from abet_alerts table
 * Shows: Recent threshold breaches with severity, metric values, timestamps
 * Data source: abet-metrics-collector /alerts endpoint → Supabase abet_alerts
 * Refresh: 60s
 * CSS prefix: ahw-
 */

import { useState, useEffect, useCallback } from 'react'
import { fetchAlertHistory } from '../lib/api'
import { AlertTriangle } from 'lucide-react'
import './AlertHistoryWidget.css'

function timeAgo(ts) {
  const diff = Date.now() - new Date(ts).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

export default function AlertHistoryWidget() {
  const [alerts, setAlerts] = useState([])
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    const res = await fetchAlertHistory(72) // 3 days
    if (res.status === 'ok') {
      setAlerts(res.data || [])
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchData()
    const id = setInterval(fetchData, 60000)
    return () => clearInterval(id)
  }, [fetchData])

  return (
    <div className="ahw-container">
      <div className="ahw-header">
        <AlertTriangle size={16} color="#eab308" />
        <h3>Alert History</h3>
        <span className="ahw-count">{alerts.length} alerts (72h)</span>
      </div>

      {loading && !alerts.length ? (
        <div className="ahw-empty">Loading alerts...</div>
      ) : !alerts.length ? (
        <div className="ahw-empty">No alerts in the last 72 hours. All systems normal.</div>
      ) : (
        <div className="ahw-list">
          {alerts.map((alert, i) => {
            const threshold = alert.threshold || {}
            const severity = threshold.severity || 'warning'

            return (
              <div key={i} className={`ahw-alert ${severity}`}>
                <div className="ahw-alert-header">
                  <span className="ahw-alert-name">
                    {severity === 'critical' ? '🚨' : '⚠️'} {threshold.message || alert.alert_name || 'Unknown alert'}
                  </span>
                  <span className="ahw-alert-time">{timeAgo(alert.ts)}</span>
                </div>
                <div className="ahw-alert-detail">
                  Metric: <code>{threshold.metric || '?'}</code> ={' '}
                  <span className="ahw-alert-value">{alert.metric_value ?? '?'}</span>
                  {' '}(threshold: {threshold.condition} {threshold.value})
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
